import { Component, Input, Output, EventEmitter, ViewChildren, QueryList, OnChanges, SimpleChanges, TrackByFunction } from '@angular/core';
import { FormGroup, FormArray, FormBuilder, Validators, AbstractControl } from '@angular/forms';

import { CustomField, CustomFieldType, CustomFieldValue, duplicateKeyValidator, invalidCharacterValidator, revealInvalid } from './info.utils';
import { generateElementId } from '../../shared/string.utils';

@Component({
  selector: 'info-property',
  styleUrls: ['./info-edit.component.scss'],
  templateUrl: './info-property.component.html',
})
export class InfoPropertyComponent implements OnChanges {
  @Input() field: AbstractControl;
  @Input() index: number
  @Input() parent?: AbstractControl;
  @Input() writable = false
  @Output() delete = new EventEmitter<CustomField>()

  @ViewChildren('info-property') childNodes: QueryList<InfoPropertyComponent> // accessed by info-edit

  expanded = false

  get invalid(): boolean {
    return this.field.invalid;
  }

  get dirty(): boolean {
    return this.field.dirty;
  }

  get fieldType(): string {
    return this.field.get('type').value;
  }

  get isArrayOrObject(): boolean {
    return this.fieldType === 'array' || this.fieldType === 'object';
  }

  get formArray(): FormArray | null {
    return this.isArrayOrObject || this.fieldType === 'root' ? this.field.get('value') as FormArray : null;
  }

  get hasInvalidMember(): boolean {
    return this.formArray?.touched && this.formArray?.invalid;
  }

  get isArrayMember(): boolean {
    if (this.parent) {
      return this.parent.get('type').value === 'array';
    }
    return false;
  }

  get typeIconClass(): string {
    switch (this.fieldType) {
      case 'object':
        return 'data_object';
      case 'array':
        return 'list';
      case 'boolean':
        return 'toggle_on';
      case 'number':
        return '123';
      default:
        return 'text_fields';
    }
  }

  constructor(private fb: FormBuilder) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.field
      && (changes.field.firstChange
        || changes.field.currentValue.get('key').value !== changes.field.previousValue.get('key').value)) {
      this.expanded = this.isArrayOrObject && this.formArray?.length === 0 || this.fieldType === 'root';
    }
  }

  memberTrackBy: TrackByFunction<FormGroup> = (index, member) => member.get('uid').value

  addMember(type: CustomFieldType): void {
    const insertIndex = 0;
    let value: CustomFieldValue | FormArray = '';
    if (type === 'array' || type === 'object') {
      value = this.fb.array([], { validators: [duplicateKeyValidator] });
    } else if (type === 'number') {
      value = 0;
    } else if (type === 'boolean') {
      value = true;
    }
    const group = this.fb.group({
      uid: generateElementId(),
      type,
      key: ['', this.fieldType === 'array' ? [] : [Validators.required, invalidCharacterValidator]],
      value,
    });
    const members = this.field.get('value') as FormArray;
    members.insert(insertIndex, group);
    members.markAsDirty();
    this.expanded = true;
  }

  deleteMember(removeIndex: number): void {
    const members = this.field.get('value') as FormArray;
    members.markAsDirty();
    members.removeAt(removeIndex);
  }

  revealInvalidDescendants(): void {
    revealInvalid([this]);
  }
}
